import React, { useEffect, useRef, useState } from "react";
import { Platform, ScrollView, StyleSheet, TouchableWithoutFeedback, View } from "react-native";
import { CheckCircle2 } from "lucide-react";
import { ConnectorAuthMode, ConnectorCategory } from "@axio/graphql/generated/graphql";
import Pill from "../../../common/Pill/Pill";
import CustomModal from "../../../common/general/CustomModal/CustomModal";
import CustomText from "../../../common/general/CustomText/CustomText";
import {
  getDefaultSetupAuthMode,
  getDefaultSetupStrategy,
  getConnectorConnectionPattern,
  getSavedConfigurationActionLabel,
  getSavedConfigurationSummary,
  type CatalogConnector,
  type ConnectorActionResult,
  type ConnectorApprovalSummary,
  type ConnectorCredentialConfig,
  type InstitutionalConnectionSummary,
  type SavedConfigurationSummary,
  usesSavedConfigurationRedirect,
  usesConnectorApprovalFlow,
} from "../catalog.utils";
import { AppApprovalContent } from "./AppApprovalContent";
import { ConfirmModal } from "./ConfirmModal";
import { ConnectorDetailsFooter } from "./ConnectorDetailsFooter";
import { ConnectorDetailsHeader } from "./ConnectorDetailsHeader";
import { InstitutionalConnected, InstitutionalSetup } from "./InstitutionalContent";
import { PersonalOnlyContent } from "./PersonalOnlyContent";

type PendingAction = "approve" | "activate" | "disconnect" | "revoke" | "test" | null;

interface ConnectorDetailsProps {
  connector: CatalogConnector | null;
  connection: InstitutionalConnectionSummary | null;
  approval: ConnectorApprovalSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApproveApp: (connector: CatalogConnector) => Promise<ConnectorActionResult>;
  onActivate: (
    connector: CatalogConnector,
    authMode: ConnectorAuthMode,
    config: ConnectorCredentialConfig
  ) => Promise<ConnectorActionResult>;
  onTestConnection: (
    connector: CatalogConnector,
    authMode: ConnectorAuthMode,
    config: ConnectorCredentialConfig
  ) => Promise<ConnectorActionResult>;
  onDisconnect: (
    connector: CatalogConnector,
    connection: InstitutionalConnectionSummary
  ) => Promise<ConnectorActionResult>;
  onRevokeApproval: (connector: CatalogConnector, approval: ConnectorApprovalSummary) => Promise<ConnectorActionResult>;
}

type PillType = {
  text: string;
  backgroundColor: string;
  textColor: string;
  borderColor: string;
  icon?: JSX.Element;
};

export const PILL_TYPES: Record<string, PillType> = {
  active: {
    text: "Active",
    backgroundColor: "#F3FEE7",
    textColor: "#3B7C0F",
    borderColor: "#86EFAC40",
  },
  approved: {
    text: "Approved",
    backgroundColor: "#F3FEE7",
    textColor: "#3B7C0F",
    borderColor: "#86EFAC40",
    icon: <CheckCircle2 size={12} color="#16A34A" />,
  },
  working: {
    text: "Working",
    backgroundColor: "#EFF8FF",
    textColor: "#175CD3",
    borderColor: "#B2DDFF",
    icon: <CheckCircle2 size={12} color="#175CD3" />,
  },
  degraded: {
    text: "Degraded",
    backgroundColor: "#FFFAEB",
    textColor: "#B54708",
    borderColor: "#FCD34D40",
  },
  failed: {
    text: "Failed",
    backgroundColor: "#FEE2E2",
    textColor: "#B91C1C",
    borderColor: "#FCA5A540",
  },
  expired: {
    text: "Expired",
    backgroundColor: "#FEE2E2",
    textColor: "#B91C1C",
    borderColor: "#FCA5A540",
  },
  "setup-required": {
    text: "Setup Required",
    backgroundColor: "#F2F4F7",
    textColor: "#101828",
    borderColor: "#EAECF0",
  },
  "personal-only": {
    text: "Personal",
    backgroundColor: "",
    textColor: "#101828",
    borderColor: "#EAECF0",
  },
  [ConnectorCategory.CloudStorage]: {
    text: "Cloud Storage",
    backgroundColor: "#E6EBFF",
    textColor: "#3459D4",
    borderColor: "",
  },
  [ConnectorCategory.VideoService]: {
    text: "Video",
    backgroundColor: "#F5F0FE",
    textColor: "#7244BB",
    borderColor: "",
  },
  [ConnectorCategory.LmsRepository]: {
    text: "LMS",
    backgroundColor: "#E8F5F7",
    textColor: "#15727D",
    borderColor: "",
  },
  [ConnectorCategory.AdminKnowledgeBase]: {
    text: "Knowledge Base",
    backgroundColor: "#E6F3FF",
    textColor: "#0069CC",
    borderColor: "",
  },
  [ConnectorCategory.LibraryRepository]: {
    text: "Library Repository",
    backgroundColor: "#FFF7ED",
    textColor: "#C2410C",
    borderColor: "",
  },
  [ConnectorCategory.WebCms]: {
    text: "Web CMS",
    backgroundColor: "#ECFDF3",
    textColor: "#027A48",
    borderColor: "",
  },
};

const getActionFailureMessage = (action: PendingAction): string => {
  switch (action) {
    case "approve":
      return "We were not able to approve this connector.";
    case "activate":
      return "We were not able to activate this connector.";
    case "test":
      return "We were not able to test this connector.";
    case "disconnect":
      return "We were not able to disconnect this connector.";
    case "revoke":
      return "We were not able to revoke this approval.";
    case null:
      return "We were not able to complete this action.";
  }
};

const getSavedConfigurationTestKey = (savedConfiguration: SavedConfigurationSummary): string =>
  JSON.stringify({
    authMode: savedConfiguration.authMode,
    fields: savedConfiguration.fields
      .map(({ key, isSecret, value }) => ({ key, isSecret, value: value ?? "" }))
      .sort((leftField, rightField) => leftField.key.localeCompare(rightField.key)),
  });

export function ConnectorDetails({
  connector,
  connection,
  approval,
  open,
  onOpenChange,
  onApproveApp,
  onActivate,
  onTestConnection,
  onDisconnect,
  onRevokeApproval,
}: ConnectorDetailsProps) {
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [isActivated, setIsActivated] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [actionErrorMessage, setActionErrorMessage] = useState<string | null>(null);
  const [actionSuccessMessage, setActionSuccessMessage] = useState<string | null>(null);
  const [hasSuccessfulCurrentSetupTest, setHasSuccessfulCurrentSetupTest] = useState(false);
  const [lastSuccessfulSavedConfigurationKey, setLastSuccessfulSavedConfigurationKey] = useState<string | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const setupSubmitRef = useRef<(() => Promise<{ config: ConnectorCredentialConfig; isValid: boolean }>) | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    setPendingAction(null);
    setIsActivated(false);
    setShowDisconnectConfirm(false);
    setActionErrorMessage(null);
    setActionSuccessMessage(null);
    setHasSuccessfulCurrentSetupTest(false);
    setLastSuccessfulSavedConfigurationKey(null);
  }, [connector?.id, open]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const handleSetupSubmitRef = (submit: () => Promise<{ config: ConnectorCredentialConfig; isValid: boolean }>) => {
    setupSubmitRef.current = submit;
  };

  if (!connector) {
    return null;
  }

  const connectionPattern = getConnectorConnectionPattern(connector);
  const isConnected = connection !== null;
  const setupStrategy = getDefaultSetupStrategy(connector);
  const setupAuthMode = getDefaultSetupAuthMode(connector);
  const showsApproval = usesConnectorApprovalFlow(connector);
  const savedConfiguration = getSavedConfigurationSummary(connector);
  const actionAuthMode = savedConfiguration?.authMode ?? setupAuthMode;
  const savedConfigurationTestKey = savedConfiguration ? getSavedConfigurationTestKey(savedConfiguration) : null;
  const requiresTestBeforeActivation =
    connectionPattern === "setup-required" &&
    (!savedConfiguration || actionAuthMode === ConnectorAuthMode.SessionToken);
  const canActivate = !requiresTestBeforeActivation
    ? true
    : isConnected
      ? true
      : savedConfiguration
        ? lastSuccessfulSavedConfigurationKey === savedConfigurationTestKey
        : hasSuccessfulCurrentSetupTest;
  const savedConfigurationActionLabel = getSavedConfigurationActionLabel(connector);
  const connectedInstitutionalActionLabel =
    savedConfiguration && !usesSavedConfigurationRedirect(connector) ? savedConfigurationActionLabel : "Refresh Status";
  const connectedInstitutionalPendingLabel =
    savedConfiguration && !usesSavedConfigurationRedirect(connector) ? "Testing connection..." : "Refreshing...";

  const closeAfterSuccess = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }

    closeTimeoutRef.current = setTimeout(() => {
      onOpenChange(false);
    }, 1200);
  };

  const runAction = async (
    action: Exclude<PendingAction, null>,
    callback: () => Promise<ConnectorActionResult>
  ): Promise<void> => {
    setPendingAction(action);
    setActionErrorMessage(null);
    setActionSuccessMessage(null);

    try {
      const result = await callback();

      if (!result.success) {
        setActionErrorMessage(result.message ?? getActionFailureMessage(action));
        return;
      }

      if (action === "approve") {
        setIsActivated(true);
        closeAfterSuccess();
        return;
      }

      if (action === "activate") {
        setIsActivated(true);
        return;
      }

      onOpenChange(false);
    } catch (actionError) {
      setActionErrorMessage(actionError instanceof Error ? actionError.message : getActionFailureMessage(action));
    } finally {
      setPendingAction(null);
    }
  };

  const handleActivate = async (config: ConnectorCredentialConfig) => {
    if (!actionAuthMode) {
      return;
    }

    await runAction("activate", () => onActivate(connector, actionAuthMode, config));
  };

  const handleSetupConfigChange = () => {
    setHasSuccessfulCurrentSetupTest(false);
    setActionSuccessMessage(null);
  };

  const handleTestConnection = async () => {
    if (!actionAuthMode) {
      return;
    }

    let config: ConnectorCredentialConfig = {};

    if (savedConfiguration) {
      setHasSuccessfulCurrentSetupTest(false);
    } else {
      const setupSubmission = await setupSubmitRef.current?.();

      if (!setupSubmission?.isValid) {
        return;
      }

      config = setupSubmission.config;
      setLastSuccessfulSavedConfigurationKey(null);
    }

    setPendingAction("test");
    setActionErrorMessage(null);
    setActionSuccessMessage(null);

    try {
      const result = await onTestConnection(connector, actionAuthMode, config);

      if (!result.success) {
        setHasSuccessfulCurrentSetupTest(false);
        setLastSuccessfulSavedConfigurationKey(null);
        setActionErrorMessage(result.message ?? "We were not able to test this connector.");
        setActionSuccessMessage(null);
        return;
      }

      setActionSuccessMessage(result.message ?? "Connection test succeeded. You can activate this connector now.");

      if (!savedConfiguration && actionAuthMode === ConnectorAuthMode.UserOauth2) {
        if (!result.authorizationUrl) {
          setHasSuccessfulCurrentSetupTest(false);
          setLastSuccessfulSavedConfigurationKey(null);
          setActionSuccessMessage(null);
          setActionErrorMessage("OAuth approval URL is not available for this connector.");
          return;
        }

        if (typeof window !== "undefined") {
          window.location.href = result.authorizationUrl;
        }
        return;
      }

      if (savedConfiguration) {
        setLastSuccessfulSavedConfigurationKey(savedConfigurationTestKey);
        return;
      }

      setHasSuccessfulCurrentSetupTest(true);
    } catch (actionError) {
      setHasSuccessfulCurrentSetupTest(false);
      setLastSuccessfulSavedConfigurationKey(null);
      setActionSuccessMessage(null);
      setActionErrorMessage(
        actionError instanceof Error ? actionError.message : "We were not able to test this connector."
      );
    } finally {
      setPendingAction(null);
    }
  };

  const handleSavedConfigurationAction = async () => {
    if (!savedConfiguration || !actionAuthMode) {
      return;
    }

    if (!usesSavedConfigurationRedirect(connector)) {
      await handleActivate({});
      return;
    }

    if (typeof window === "undefined") {
      return;
    }

    if (!savedConfiguration?.authorizationUrl) {
      setActionErrorMessage(`${savedConfigurationActionLabel} URL is not available for this connector.`);
      return;
    }

    window.location.href = savedConfiguration.authorizationUrl;
  };

  const handleConnectedInstitutionalAction = async () => {
    if (savedConfiguration && !usesSavedConfigurationRedirect(connector)) {
      await handleTestConnection();
      return;
    }

    handleUnavailableInstitutionalAction("sync");
  };

  const handleFooterActivate = async () => {
    if (!canActivate) {
      return;
    }

    if (savedConfiguration) {
      await handleSavedConfigurationAction();
      return;
    }

    const setupSubmission = await setupSubmitRef.current?.();

    if (!setupSubmission?.isValid) {
      return;
    }

    await handleActivate(setupSubmission.config);
  };

  const handleApproveApp = async () => {
    await runAction("approve", () => onApproveApp(connector));
  };

  const handleConfirmDisconnect = async () => {
    if (!connection) {
      return;
    }

    setShowDisconnectConfirm(false);
    await runAction("disconnect", () => onDisconnect(connector, connection));
  };

  const handleRevokeApproval = async () => {
    if (!approval) {
      return;
    }

    await runAction("revoke", () => onRevokeApproval(connector, approval));
  };

  const handleUnavailableInstitutionalAction = (action: "sync" | "reauthenticate") => {
    if (action === "sync") {
      setActionErrorMessage("Manual refresh is not available from this screen yet.");
      return;
    }

    setActionErrorMessage("Re-authentication is not available from this screen yet.");
  };

  return (
    <>
      <CustomModal
        visible={open}
        onRequestClose={() => onOpenChange(false)}
        style={{ maxWidth: 600 }}
        bodyStyle={styles.modalBody}
        containerStyle={{ padding: 0 }}>
        <TouchableWithoutFeedback>
          <View>
            <TouchableWithoutFeedback>
              <View style={styles.sheet}>
                <ConnectorDetailsHeader
                  connector={connector}
                  connectionStatus={connection?.status ?? approval?.status ?? null}
                  hasApproval={approval !== null}
                  onClose={() => onOpenChange(false)}
                />
                <ScrollView
                  style={styles.body}
                  contentContainerStyle={styles.bodyContent}
                  showsVerticalScrollIndicator={false}
                  keyboardShouldPersistTaps="handled">
                  <CustomText
                    text={connector.description}
                    size="s"
                    useTranslationText={false}
                    textType="text"
                    weight="normal"
                    role="text"
                    style={styles.description}
                  />

                  {connectionPattern === "personal-only" && <PersonalOnlyContent connectorName={connector.name} />}

                  {approval && <AppApprovalContent connectorName={connector.name} approval={approval} />}

                  {!showsApproval && isConnected && (
                    <InstitutionalConnected
                      connection={connection}
                      isSyncing={pendingAction === "test"}
                      isReauthenticating={false}
                      onSyncNow={handleConnectedInstitutionalAction}
                      onReauthenticate={() => handleUnavailableInstitutionalAction("reauthenticate")}
                      syncActionLabel={connectedInstitutionalActionLabel}
                      syncPendingLabel={connectedInstitutionalPendingLabel}
                      showSyncAction={!(savedConfiguration && !usesSavedConfigurationRedirect(connector))}
                      disableSyncAction={pendingAction === "test"}
                    />
                  )}

                  {savedConfiguration && (
                    <View style={styles.savedConfigurationCard}>
                      {savedConfiguration.fields.map((field) => (
                        <View key={field.key} style={styles.savedConfigurationRow}>
                          <CustomText
                            text={field.label}
                            size="xs"
                            useTranslationText={false}
                            textType="text"
                            weight="medium"
                            role="text"
                            style={styles.savedConfigurationLabel}
                          />
                          <CustomText
                            text={field.value}
                            size="s"
                            useTranslationText={false}
                            textType="text"
                            weight="normal"
                            role="text"
                            style={styles.savedConfigurationValue}
                          />
                        </View>
                      ))}
                    </View>
                  )}

                  {connectionPattern === "setup-required" &&
                    !isConnected &&
                    !savedConfiguration &&
                    setupStrategy &&
                    setupAuthMode && (
                      <InstitutionalSetup
                        fields={setupStrategy.credentialFields}
                        onSubmitRef={handleSetupSubmitRef}
                        onConfigChange={handleSetupConfigChange}
                      />
                    )}

                  {actionErrorMessage && (
                    <Pill
                      text={actionErrorMessage}
                      backgroundColor={PILL_TYPES.failed.backgroundColor}
                      textColor={PILL_TYPES.failed.textColor}
                      textSize="xs"
                      updated={true}
                      borderColor={PILL_TYPES.failed.textColor}
                      borderWidth={1}
                      containerStyle={styles.errorPill}
                    />
                  )}
                  {actionSuccessMessage && (
                    <Pill
                      text={actionSuccessMessage}
                      backgroundColor={PILL_TYPES.active.backgroundColor}
                      textColor={PILL_TYPES.active.textColor}
                      textSize="xs"
                      updated={true}
                      borderColor={PILL_TYPES.active.textColor}
                      borderWidth={1}
                      containerStyle={styles.feedbackPill}
                    />
                  )}
                </ScrollView>
                <ConnectorDetailsFooter
                  connector={connector}
                  connection={connection}
                  approval={approval}
                  pendingAction={pendingAction}
                  isActivated={isActivated}
                  canActivate={canActivate}
                  requireTestBeforeActivation={requiresTestBeforeActivation}
                  onClose={() => onOpenChange(false)}
                  onApproveApp={handleApproveApp}
                  onTestConnection={handleTestConnection}
                  onActivate={handleFooterActivate}
                  onDisconnect={() => setShowDisconnectConfirm(true)}
                  onRevokeApproval={handleRevokeApproval}
                />
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </CustomModal>

      <ConfirmModal
        open={showDisconnectConfirm}
        onOpenChange={setShowDisconnectConfirm}
        onConfirm={
          savedConfiguration && !isConnected
            ? async () => {
                setShowDisconnectConfirm(false);
                await runAction("disconnect", () =>
                  onDisconnect(
                    connector,
                    connection ?? {
                      id: connector.id,
                      connectorId: connector.id,
                      status: "active",
                    }
                  )
                );
              }
            : handleConfirmDisconnect
        }
        title={`${savedConfiguration && !isConnected ? "Delete" : "Disconnect"} ${connector.name}?`}
        description={
          savedConfiguration && !isConnected
            ? `This will remove the saved ${connector.name} configuration for your institution. You will need to re-add credentials before anyone can connect again.`
            : connection && typeof connection.userCount === "number"
              ? `This will disconnect ${connector.name} for all ${connection.userCount.toLocaleString()} users at your institution. Any previously imported content will remain, but live queries will stop immediately.`
              : `This will disconnect ${connector.name} for your institution. Any previously imported content will remain, but live queries will stop immediately.`
        }
        confirmText={savedConfiguration && !isConnected ? "Delete configuration" : "Disconnect"}
        variant="destructive"
      />
    </>
  );
}

const styles = StyleSheet.create({
  sheet: {
    backgroundColor: "#FFFFFF",
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: "100%",
    ...Platform.select({
      ios: {
        shadowColor: "#000",
        shadowOffset: { width: 0, height: -3 },
        shadowOpacity: 0.12,
        shadowRadius: 12,
      },
      android: { elevation: 16 },
    }),
  },
  body: {
    flex: 1,
    minHeight: 360,
    maxHeight: 560,
  },
  modalBody: {
    flexGrow: 0,
  },
  bodyContent: {
    paddingHorizontal: 24,
    paddingVertical: 20,
  },
  description: {
    color: "#344054",
  },
  feedbackPill: {
    borderRadius: 16,
    paddingHorizontal: 12,
    paddingVertical: 8,
    alignSelf: "flex-start",
    marginTop: 20,
    width: "100%",
  },
  errorPill: {
    borderRadius: 16,
    paddingHorizontal: 12,
    paddingVertical: 8,
    alignSelf: "flex-start",
    marginTop: 20,
    width: "100%",
  },
  savedConfigurationCard: {
    marginTop: 20,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 12,
    padding: 16,
    gap: 12,
  },
  savedConfigurationRow: {
    gap: 4,
  },
  savedConfigurationLabel: {
    color: "#667085",
  },
  savedConfigurationValue: {
    color: "#101828",
  },
});
